import React, { useState, useEffect, useCallback } from 'react'
import { Certificates, ClientCertificate, FileData } from '@/types'
import { toastSuccess } from '@/utils/toast'
import { ClientCertificateSection } from './ClientCertificateSection'
import { RootCertificatesSection } from './RootCertificatesSection'
import { IntermediateCertificatesSection } from './IntermediateCertificatesSection'

interface CertificatesSectionProps {
  initialCertificates?: Certificates
  onSave: (certificates: Certificates) => Promise<void>
  readOnly?: boolean
}

export const CertificatesSection: React.FC<CertificatesSectionProps> = ({
  initialCertificates,
  onSave,
  readOnly = false,
}) => {
  // Store the last submitted state separately from working state
  const [submittedCertificates, setSubmittedCertificates] = useState<Certificates>({
    rootCertificates: [],
    intermediateCertificates: [],
    clientCertificate: undefined,
  })

  useEffect(() => {
    // Only update from external source (after save from parent)
    if (initialCertificates) {
      setSubmittedCertificates(initialCertificates)
    }
  }, [initialCertificates])


  const handleClientCertificateSave = useCallback(
    async (clientCertificate: ClientCertificate) => {
      // Submit only clientCertificate field
      await onSave({
        clientCertificate,
      })
      // Update submitted state after successful save
      setSubmittedCertificates(prev => ({
        ...prev,
        clientCertificate,
      }))
      toastSuccess('Certificat client mis à jour avec succès')
    },
    [onSave]
  )

  const handleRootCertificatesSave = useCallback(
    async (rootCertificates: FileData[]) => {
      // Submit only rootCertificates field
      await onSave({
        rootCertificates,
      })
      // Update submitted state after successful save
      setSubmittedCertificates(prev => ({
        ...prev,
        rootCertificates,
      }))
      toastSuccess('Certificats racine mis à jour avec succès')
    },
    [onSave]
  )

  const handleIntermediateCertificatesSave = useCallback(
    async (intermediateCertificates: FileData[]) => {
      // Submit only intermediateCertificates field
      await onSave({
        intermediateCertificates,
      })
      // Update submitted state after successful save
      setSubmittedCertificates(prev => ({
        ...prev,
        intermediateCertificates,
      }))
      toastSuccess('Certificats intermédiaires mis à jour avec succès')
    },
    [onSave]
  )

  return (
    <div className="space-y-6">
      <ClientCertificateSection
        initialCertificate={submittedCertificates.clientCertificate}
        onSave={handleClientCertificateSave}
        readOnly={readOnly}
      />
      <RootCertificatesSection
        initialCertificates={submittedCertificates.rootCertificates}
        onSave={handleRootCertificatesSave}
        readOnly={readOnly}
      />
      <IntermediateCertificatesSection
        initialCertificates={submittedCertificates.intermediateCertificates}
        onSave={handleIntermediateCertificatesSave}
        readOnly={readOnly}
      />
    </div>
  )
}
